import { FormEvent } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { AgentChatMessageFragment } from '@treams/treams-network';
import { Avatar, FlexGap, Heading, space, tokens } from '@treams/treams-ui';

import { AgentChatComposer } from './AgentChatComposer';
import { AgentChatEmpty } from './AgentChatEmpty';
import { AgentChatMessageList } from './AgentChatMessageList';

const EMPTY_WIDTH = 640;
const THREAD_WIDTH = 720;

const ThreadContainer = styled(Flex)`
	flex: 1;
	min-height: 0;
	height: 100%;
	flex-direction: column;
	background: ${tokens.surface.white};
`;

const Column = styled(Box)<{ $maxWidth: number }>`
	width: 100%;
	max-width: ${(props) => props.$maxWidth}px;
	margin-inline: auto;
`;

const EmptyPane = styled(Flex)`
	flex: 1;
	min-height: 0;
	flex-direction: column;
	overflow-y: auto;
	padding: ${space['3xl']}px ${tokens.padding.page}px;
`;

const ThreadInner = styled(Flex)`
	flex: 1;
	min-height: 0;
	flex-direction: column;
`;

const ComposerBar = styled(Box)`
	position: sticky;
	bottom: 0;
	z-index: 1;
	background: ${tokens.surface.white};
	padding: ${tokens.gap.group}px 0 ${tokens.padding.page}px;
`;

const SubjectHeader = styled(FlexGap)`
	flex-direction: column;
	align-items: center;
	padding: ${tokens.padding.page}px ${tokens.padding.page}px 0;
`;

export type AgentChatSubject = {
	id: string;
	firstName: string;
	lastName: string;
	avatar?: { source: { url: string } } | null;
} | null;

export function AgentChatThread(props: {
	messages: AgentChatMessageFragment[];
	isLoading?: boolean;
	isStreaming: boolean;
	error?: string;
	draft: string;
	onDraftChange: (value: string) => void;
	onSubmit: (event: FormEvent) => void;
	onSelectSuggestion: (message: string, subjectID?: string) => void;
	onStartOver: () => void;
	showComposer: boolean;
	estimatedCost?: number;
	subject?: AgentChatSubject;
}) {
	const { t } = useTranslation();
	const isEmpty = props.messages.length === 0 && !props.isStreaming && !props.isLoading;
	const subjectName = props.subject
		? `${props.subject.firstName} ${props.subject.lastName}`.trim()
		: undefined;

	const composer = props.showComposer ? (
		<AgentChatComposer
			draft={props.draft}
			disabled={props.isStreaming}
			placeholder={t('aiAgent.askPlaceholder')}
			onChange={props.onDraftChange}
			onSubmit={props.onSubmit}
		/>
	) : undefined;

	return (
		<ThreadContainer>
			{isEmpty ? (
				<EmptyPane>
					<Column $maxWidth={EMPTY_WIDTH}>
						<AgentChatEmpty composer={composer} onSelectSuggestion={props.onSelectSuggestion} />
					</Column>
				</EmptyPane>
			) : (
				<ThreadInner>
					{props.subject && subjectName && (
						<SubjectHeader gap={tokens.gap.group}>
							<Avatar size={48} user={props.subject} />
							<Heading>
								<Trans
									i18nKey="aiAgent.forSubject"
									components={{ strong: <strong /> }}
									values={{ name: subjectName }}
								/>
							</Heading>
						</SubjectHeader>
					)}
					<AgentChatMessageList
						messages={props.messages}
						isLoading={props.isLoading}
						isStreaming={props.isStreaming}
						error={props.error}
						onSelectSuggestion={props.onSelectSuggestion}
						onStartOver={props.onStartOver}
						estimatedCost={props.estimatedCost}
					/>
					{composer && (
						<ComposerBar>
							<Column $maxWidth={THREAD_WIDTH}>{composer}</Column>
						</ComposerBar>
					)}
				</ThreadInner>
			)}
		</ThreadContainer>
	);
}
